import { useGetFlaggedOrglessAccounts } from "@fp/admin-api-client/orgless-accounts/orgless-accounts.client";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { formatLocalDate } from "@fp/ui/components/common/local-date-time";
import { createFileRoute } from "@tanstack/react-router";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { redirectUnlessCapability } from "@/lib/organization-capabilities";

export const Route = createFileRoute("/system/orgless-accounts")({
  beforeLoad: ({ context: { queryClient } }) =>
    redirectUnlessCapability(queryClient, "canUseSensitiveDevTools"),
  component: RouteComponent,
  pendingComponent: () => <PageLoading />,
});

function formatSignupToLink(signedUpDate: string, linkedDate: string) {
  const minutes = Math.round(
    (new Date(linkedDate).getTime() - new Date(signedUpDate).getTime()) / 60000,
  );

  if (minutes < 60) return `${minutes} min`;

  return `${(minutes / 60).toFixed(1)} hr`;
}

function RouteComponent() {
  const { data, isLoading } = useGetFlaggedOrglessAccounts();
  const flagged = data?.data ?? [];

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Orgless Accounts"
        parent={{ url: "/dashboard", label: "Home" }}
      />
      <PageContentContainer className="space-y-4">
        {isLoading ? (
          <PageLoading />
        ) : flagged.length > 0 ? (
          <div className="space-y-4">
            {flagged.map((account) => (
              <div
                key={account.userId}
                className="bg-white border border-gray-200 rounded-lg p-4 space-y-2"
              >
                <div className="font-medium text-gray-900">{account.displayName}</div>
                {account.businessName && (
                  <div className="text-sm text-gray-700">{account.fullName}</div>
                )}
                <div className="text-sm text-gray-600">{account.email}</div>
                <div className="text-sm text-gray-700">
                  Linked {account.institutionName} on{" "}
                  {formatLocalDate(account.linkedDate, "dateWithYear")},{" "}
                  {formatSignupToLink(account.signedUpDate, account.linkedDate)} after
                  signing up
                </div>
                <div className="text-xs text-gray-500">
                  Flagged {formatLocalDate(account.flaggedDate, "dateWithYear")} ·
                  Last seen{" "}
                  {account.lastSeenDate
                    ? formatLocalDate(account.lastSeenDate, "dateWithYear")
                    : "never"}
                </div>
                <div className="text-xs text-gray-400">{account.userId}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-500 mb-2">No flagged accounts</div>
            <p className="text-sm text-gray-400">
              Accounts that link a bank without any relationship to a funded
              organization appear here for review.
            </p>
          </div>
        )}
      </PageContentContainer>
    </AdminLayout>
  );
}
